<template>
    <article>
        <p>
            Kubernetes automates many aspects of infrastructure management, such as resource management, rolling updates, autoscaling, etc.
            However, we should not rely on a particular feature until we understand how it works and what its limitations are.
            For instance, many engineers believe that the Kubernetes HPA (Horizontal Pod Autoscaler) can <strong>instantly</strong> scale
            their applications once the load increases. Let's check if this is the case and measure the actual HPA delay.
        </p>

        <v-img max-width="80%" class="ma-auto my-6" src="/static/img/blog/hpa.svg" />

        <p>
            The HorizontalPodAutoscaler is a Kubernetes controller (a part of <var>kube-controller-manager</var>) and the relevant API resource.
            The controller can adjust the number of desired replicas for a particular Deployment or StatefulSet based on
            its metrics obtained from the metrics API.
        </p>

        <p>
            <var>Metrics-server</var> is an add-on that collects resource metrics from <var>kubelets</var> and exposes
            them to <var>kube-apiserver</var> through the metrics API.
        </p>

        <h2>The lab</h2>

        <p>
            For this experiment, I provisioned a Kubernetes cluster on Amazon EKS.
            Managed Kubernetes clusters, such as EKS, GKE, and AKS, don't allow users to configure their control planes.
            In other words, we cannot change the parameters of <var>kube-apiserver</var>, <var>kube-controller-manager</var>,
            and <var>kube-scheduler</var>.
        </p>

        <p>
            I'll use the <var>image-resizer</var> Deployment as my test application.
            It has a CPU limit of <var>500m</var> (1/2 of a CPU core).
        </p>

        <pre v-hl.yaml>
apiVersion: apps/v1
kind: Deployment
metadata:
  name: image-resizer
spec:
  selector:
    matchLabels: {name: image-resizer}
  replicas: 2
  template:
    metadata:
      labels: {name: image-resizer}
      annotations:
        prometheus.io/scrape: 'true'
        prometheus.io/port: '80'
    spec:
      containers:
        - name: image-resizer
          image: failurepedia/golang-app:0.35
          command: ["/golang-app", "-l", ":80"]
          resources:
            limits: {cpu: "500m", memory: "512M"}
            requests: {cpu: "500m", memory: "512M"}
          ports:
            - containerPort: 80
              name: http
          readinessProbe:
            httpGet: {path: /healthz, port: 80}
            initialDelaySeconds: 1
            periodSeconds: 1
        </pre>

        <p>
            The HPA is configured to increase the number of replicas if the pod CPU usage exceeds 50%:
        </p>

        <pre v-hl.yaml>
apiVersion: autoscaling/v1
kind: HorizontalPodAutoscaler
metadata:
  name: image-resizer
  namespace: default
spec:
  maxReplicas: 3
  minReplicas: 2
  scaleTargetRef:
    apiVersion: apps/v1
    kind: Deployment
    name: image-resizer
  targetCPUUtilizationPercentage: 50
        </pre>

        <p>
            In order to increase the CPU usage of the app's containers, I will gradually increase the number of simultaneous HTTP requests to the app.
        </p>


        <h2>Round 1</h2>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/kubernetes-hpa/round-1.png" />
        </v-card>

        <p>
            As you can see in the charts above, it took <strong>2 minutes and 5 seconds</strong> from reaching 50% CPU usage to scaling the app.
        </p>

        <p>
            What does the HPA reaction time depend on?
        </p>

        <p>
            The <var>kube-controller-manager</var> component has the <var>--horizontal-pod-autoscaler-sync-period</var> parameter
            which sets the period of fetching metrics for adjusting the number of replicas.
        </p>

        <pre v-hl.bash>
--horizontal-pod-autoscaler-sync-period duration
  The period for syncing the number of pods in horizontal pod autoscaler. (default 15s)
        </pre>

        <p>
            As I mentioned, it's not possible to configure the control plane of an EKS cluster.
            However, the default value for this parameter is 15s, so it's probably not an issue.
        </p>

        <p>
            On the other hand,
            <var>metrics-server</var> has the <var>--metric-resolution</var> parameter, which is set to 60s by default.
        </p>

        <pre v-hl.bash>
--metric-resolution duration
  The resolution at which metrics-server will retain metrics, must set value at least 10s. (default 1m0s)
        </pre>

        <p>
            Let's change this parameter to 10s and see how this affects our experiment.
        </p>

        <h2>Round 2</h2>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/kubernetes-hpa/round-2.png" />
        </v-card>

        <p>
            Scaling now takes only <strong>30 seconds</strong>!
        </p>

        <p>
            It is a huge difference because if we had 100 requests/second,
            it would only affect 30 * 100 = 3000 requests instead of 2 * 60 * 100 = 12000 requests in the previous case.
        </p>

        <h2>Conclusion</h2>

        <p>
            With this simple experiment, we found out how long it takes to scale an application based on resource
            metrics and how it can be optimized.
            Of course, such a drastic increase in workload doesn't often happen in real life.
            However, it is super important to keep this limitation of the Kubernetes HPA in mind.
        </p>

        <ul>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Get started with Coroot</router-link>
            </li>
            <li>
                <a href="https://community-demo.coroot.com" target="_blank">Coroot live demo</a>
            </li>
            <li>
                <a href="https://github.com/coroot/coroot-node-agent" target="_blank">node-agent <v-icon dense>mdi-github</v-icon></a> (Apache-2.0 License)
            </li>
        </ul>

    </article>
</template>

<script>
export default {
}
</script>

<style scoped>
</style>
